import { useState } from "react";
import { CalendarDays } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Selettore data Orbit: usa il calendario condiviso (domeniche/festività evidenziate via
// components/ui/calendar.jsx + lib/calendarIt.js). Il valore resta la stringa "YYYY-MM-DD".
function parseYMD(str) {
  if (!str || typeof str !== "string") return undefined;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function toYMD(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const fmtIt = new Intl.DateTimeFormat("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

// WebKit/Apple (Safari iOS + macOS, e tutti i browser iOS che usano WebKit) hanno problemi noti
// con il Popover+Calendar Radix quando è dentro un Dialog: il calendario non si apre (mentre in
// "ore indirette", NON dentro un Dialog, funziona). Su Apple e su qualsiasi dispositivo touch usiamo
// il selettore data NATIVO (<input type="date">), affidabile e con lo stesso formato "YYYY-MM-DD".
// Il calendario stilizzato resta su desktop non-Apple (mouse).
function detectNativeDate() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iP(hone|ad|od)/.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));
  const isMacSafari = /Macintosh/.test(ua) && /Version\/[\d.]+.*Safari/.test(ua)
    && !/(Chrome|Chromium|Edg|OPR|FxiOS|CriOS)/.test(ua);
  const isTouch = typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia("(pointer: coarse)").matches;
  return isIOS || isMacSafari || isTouch;
}
const USE_NATIVE_DATE = detectNativeDate();

export function DatePicker({ label, value, onChange, testid }) {
  const selected = parseYMD(value);
  // Popover controllato: alla selezione di una data valida si chiude subito il calendario.
  const [open, setOpen] = useState(false);

  if (USE_NATIVE_DATE) {
    // Input data NATIVO visibile: l'utente lo tocca e si apre il picker del sistema (affidabile su
    // iOS/Android/Safari). NON si apre da solo perché i Dialog che lo contengono usano
    // onOpenAutoFocus={preventDefault}, quindi il campo non riceve il focus all'apertura.
    return (
      <div className="space-y-1.5">
        {label && <Label>{label}</Label>}
        <div className="relative">
          <CalendarDays className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-70" />
          <input
            type="date"
            data-testid={testid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className={cn(
              "h-12 w-full rounded-md border border-input bg-background pl-9 pr-3",
              "text-base font-heading font-semibold appearance-none",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0",
              !selected && "text-muted-foreground"
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            data-testid={testid}
            className={cn(
              "h-12 w-full justify-start text-left text-base font-heading font-semibold",
              !selected && "text-muted-foreground font-normal"
            )}
          >
            <CalendarDays className="mr-2 h-4 w-4 shrink-0 opacity-70" />
            {selected ? fmtIt.format(selected) : "Seleziona data"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            weekStartsOn={1}
            selected={selected}
            defaultMonth={selected || new Date()}
            onSelect={(d) => {
              if (!d) return;
              onChange(toYMD(d));
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
